import React from "react";
import { Row, Col } from "react-bootstrap";
import { Icon } from "@iconify/react";
import styles from './AccessosWiki.module.scss';

/**
 * 💰 Wiki - API de Vendas
 * 
 * Documentação sobre como usar a API para gerenciar vendas.
 * Explica de forma simples como criar, editar e consultar vendas via API.
 * Voltada para produtores leigos (não técnica).
 */
const ApiVendasWiki = () => {
  return (
    <div className={styles.wikiAccessos}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <div className={styles.heroBadge}>
          <Icon icon="mdi:cart" />
          <span>API de Vendas</span>
        </div>
        <p className={styles.heroText}>
          Gerencie suas vendas automaticamente via API. Crie vendas, atualize status e consulte 
          informações sem precisar acessar o painel manualmente.
        </p>
      </section>

      {/* O que é a API de Vendas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que é a API de Vendas?</span>
          <p className={styles.sectionSubtitle}>
            Entenda como funciona
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:information" className={styles.infoIcon} />
          <div>
            <p>
              A <strong>API de Vendas</strong> permite que você <strong>gerencie vendas automaticamente</strong> 
              através de sistemas externos. Em vez de acessar o painel manualmente para registrar uma venda, 
              seu sistema pode fazer isso automaticamente via API.
            </p>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              Por exemplo: quando uma compra acontece no seu site, o sistema pode <strong>criar a venda 
              automaticamente</strong> na Pagmus, atualizar o status quando o pagamento for confirmado, 
              e muito mais. Tudo <strong>sem intervenção manual</strong>!
            </p>
          </div>
        </div>
      </section>

      {/* O que você pode fazer */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>O que você pode fazer?</span>
          <p className={styles.sectionSubtitle}>
            Funcionalidades disponíveis
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconSuccess}`}>
                <Icon icon="mdi:plus-circle" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeSuccess}`}>Criar</div>
                <span className={styles.cardTitle}>Criar Vendas</span>
                <p>
                  <strong>Registre vendas automaticamente</strong> quando uma compra acontece no seu 
                  sistema. Informe produto, cliente, valor e método de pagamento.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconPlatform}`}>
                <Icon icon="mdi:pencil" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgePlatform}`}>Editar</div>
                <span className={styles.cardTitle}>Atualizar Vendas</span>
                <p>
                  <strong>Atualize informações</strong> de vendas existentes, como endereço de entrega 
                  ou observações. Vendas pagas têm proteções especiais.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconWarning}`}>
                <Icon icon="mdi:update" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeWarning}`}>Status</div>
                <span className={styles.cardTitle}>Atualizar Status</span>
                <p>
                  <strong>Mude o status</strong> de pagamento ou venda automaticamente. Por exemplo: 
                  marcar como "pago" quando o pagamento for confirmado.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.participantCard}>
              <div className={`${styles.cardIcon} ${styles.iconAffiliate}`}>
                <Icon icon="mdi:eye" />
              </div>
              <div className={styles.cardContent}>
                <div className={`${styles.cardBadge} ${styles.badgeAffiliate}`}>Consultar</div>
                <span className={styles.cardTitle}>Listar e Buscar</span>
                <p>
                  <strong>Busque e liste</strong> todas as suas vendas. Use filtros para encontrar 
                  vendas específicas por data, status, produto, etc.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como funciona */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Funciona?</span>
          <p className={styles.sectionSubtitle}>
            Fluxo simplificado de uma venda via API
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:cart-plus" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Cliente Faz Compra
                </span>
                <p>
                  Um cliente faz uma compra no seu site, aplicativo ou sistema. O pagamento é processado 
                  e confirmado.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>2</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:send" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Sistema Envia para API
                </span>
                <p>
                  Seu sistema <strong>envia uma requisição</strong> para a API da Pagmus criando a venda. 
                  Informa produto, cliente, valor e método de pagamento.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>3</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:check-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  API Processa e Cria
                </span>
                <p>
                  A API <strong>valida os dados</strong>, verifica se o produto existe e está aprovado, 
                  e <strong>cria a venda</strong> na Pagmus. Retorna um ID único da venda.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>4</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:sync" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Atualiza Status (Opcional)
                </span>
                <p>
                  Quando o pagamento for confirmado, seu sistema pode <strong>atualizar o status</strong> 
                  da venda para "pago" automaticamente via API.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Informações necessárias */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Quais Informações são Necessárias?</span>
          <p className={styles.sectionSubtitle}>
            Dados obrigatórios e opcionais para criar uma venda
          </p>
        </div>

        <div className={styles.infoCard}>
          <Icon icon="mdi:file-document-edit" className={styles.infoIcon} />
          <div>
            <p>
              Para criar uma venda via API, você precisa informar:
            </p>
            <ul style={{ marginTop: '12px', marginBottom: '12px', paddingLeft: '20px' }}>
              <li><strong>ID do Produto:</strong> Qual produto foi vendido (obrigatório)</li>
              <li><strong>ID do Cliente:</strong> Quem comprou (obrigatório)</li>
              <li><strong>Valor Total:</strong> Quanto foi pago (obrigatório)</li>
              <li><strong>Método de Pagamento:</strong> Cartão, PIX, Boleto, etc. (obrigatório)</li>
            </ul>
            <p style={{ marginTop: '12px', marginBottom: 0 }}>
              <strong>Opcionalmente</strong>, você pode informar: ID do plano (se aplicável), ID do afiliado 
              (se a venda veio de um afiliado), endereço de entrega (para produtos físicos), e observações.
            </p>
          </div>
        </div>
      </section>

      {/* Regras importantes */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Regras Importantes</span>
          <p className={styles.sectionSubtitle}>
            O que você precisa saber antes de usar
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:shield-check" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Produto Deve Estar Aprovado
                </span>
                <p>
                  Você só pode criar vendas de produtos que estão <strong>aprovados e ativos</strong>. 
                  Produtos pendentes de aprovação não podem ter vendas registradas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:lock" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Vendas Pagas são Protegidas
                </span>
                <p>
                  Vendas já pagas <strong>não podem ser editadas</strong> (exceto endereço de entrega e 
                  observações). Isso protege a integridade dos dados financeiros.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:account-check" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Apenas Seus Produtos
                </span>
                <p>
                  Você só pode criar vendas de <strong>produtos que você mesmo criou</strong>. Não é 
                  possível criar vendas de produtos de outros produtores.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:currency-usd" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Comissões Automáticas
                </span>
                <p>
                  Se a venda tiver um afiliado associado, a <strong>comissão é calculada automaticamente</strong> 
                  quando a venda é criada via API.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Como acessar a documentação */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Como Acessar a Documentação Técnica?</span>
          <p className={styles.sectionSubtitle}>
            Onde encontrar exemplos e detalhes técnicos
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.configCard}>
              <div className={styles.configNumber}>1</div>
              <div className={styles.configIcon}>
                <Icon icon="mdi:menu" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Menu Lateral
                </span>
                <p>
                  No <strong>menu lateral</strong>, acesse <strong>Opções → API → Vendas</strong>. 
                  Lá você encontrará a documentação técnica completa com exemplos de código, 
                  endpoints disponíveis e como usar cada funcionalidade.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* Dicas */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Dicas e Boas Práticas</span>
          <p className={styles.sectionSubtitle}>
            Recomendações para usar a API de Vendas
          </p>
        </div>

        <Row className="g-4">
          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:test-tube" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Teste Primeiro
                </span>
                <p>
                  Sempre <strong>teste a integração</strong> em ambiente de desenvolvimento antes de 
                  usar em produção. Isso evita erros e problemas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:alert-circle" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Trate Erros
                </span>
                <p>
                  Configure seu sistema para <strong>tratar erros</strong> retornados pela API. 
                  A API sempre retorna mensagens claras sobre o que deu errado.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:clock-fast" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Atualize Status Rapidamente
                </span>
                <p>
                  Quando um pagamento for confirmado, <strong>atualize o status imediatamente</strong> 
                  via API. Isso mantém seus dados sempre sincronizados.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={6}>
            <div className={styles.configCard}>
              <div className={styles.configIcon}>
                <Icon icon="mdi:file-document-check" />
              </div>
              <div className={styles.configContent}>
                <span className={styles.configTitle}>
                  Valide Dados Antes
                </span>
                <p>
                  <strong>Valide os dados</strong> no seu sistema antes de enviar para a API. 
                  Isso evita requisições desnecessárias e erros.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>

      {/* FAQ */}
      <section className={styles.section}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionTitle}>Perguntas Frequentes</span>
          <p className={styles.sectionSubtitle}>
            Tire suas dúvidas sobre a API de Vendas
          </p>
        </div>

        <Row className="g-4">
          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Posso criar vendas de produtos que ainda não foram aprovados?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Não. Você só pode criar vendas de produtos que estão <strong>aprovados e ativos</strong>. 
                  Produtos pendentes de aprovação não podem ter vendas registradas via API.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Posso editar uma venda que já foi paga?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Vendas pagas têm <strong>proteções especiais</strong>. Você só pode editar o 
                  <strong>endereço de entrega</strong> e <strong>observações</strong>. Não é possível 
                  alterar valor, cliente, produto ou método de pagamento de vendas pagas.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>As comissões são calculadas automaticamente?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Sim! Se você informar um <strong>ID de afiliado</strong> ao criar a venda, a 
                  comissão é <strong>calculada e registrada automaticamente</strong> pela API. 
                  Você não precisa fazer nada além de informar o afiliado.
                </p>
              </div>
            </div>
          </Col>

          <Col lg={12}>
            <div className={styles.infoCard}>
              <Icon icon="mdi:help-circle" className={styles.infoIcon} />
              <div>
                <p style={{ marginBottom: '8px' }}>
                  <strong>Preciso de permissão especial para usar a API de Vendas?</strong>
                </p>
                <p style={{ marginBottom: '16px' }}>
                  Sim. Ao criar um token de API, você precisa configurar a permissão 
                  <strong>"sales:write"</strong> para criar e editar vendas. Sem essa permissão, 
                  você só poderá consultar (ler) vendas, mas não criar ou editar.
                </p>
              </div>
            </div>
          </Col>
        </Row>
      </section>
    </div>
  );
};

export default ApiVendasWiki;

